import { HttpClient } from '@angular/common/http'
import { Injectable, inject } from '@angular/core'

import { Observable, map } from 'rxjs'

import {
  GroupSolutionAccessAsset,
  GroupSolutionAccessAssetCandidate,
} from '@flowx/designer/modules/workspaces/shared/commons.types'
import { AccessManagementGroupsApiService } from '@flowx/designer/modules/workspaces/shared/groups/access-management-groups.api'
import '@flowx/designer/modules/workspaces/shared/roles'
import {
  AccessManagementAssetSummary,
  AccessManagementGroup,
  AccessManagementGroupPayload,
  AccessManagementUser,
  AccessManagementUserSummary,
} from '@flowx/designer/modules/workspaces/shared/workspace.utils'

import {
  ApiHttpService,
  DEFAULT_PAGINATION_PARAMS,
  PaginationQueryParams,
} from './api-http.service'

import { ApplicationType } from '../entities/application.entity'
import { PaginatedResponse } from '../services/paginated-datasource'

@Injectable()
export class OrganizationAdminGroupsApiService
  extends ApiHttpService
  implements AccessManagementGroupsApiService
{
  url = `auth/api/runtime/groups`

  constructor() {
    const baseUrl = inject<string>('BASE_URL' as any)
    const httpClient = inject(HttpClient)

    super(baseUrl, httpClient)
  }

  getGroups(params: PaginationQueryParams): Observable<PaginatedResponse<AccessManagementGroup>> {
    return this.get(`${this.url}/with-members-preview`, {
      params: this.addParams({ ...DEFAULT_PAGINATION_PARAMS, ...params }),
    })
  }

  createGroup(payload: AccessManagementGroupPayload): Observable<AccessManagementGroup> {
    return this.post(`${this.url}`, payload)
  }

  updateGroup(
    groupId: string,
    payload: AccessManagementGroupPayload
  ): Observable<AccessManagementGroup> {
    return this.patch(`${this.url}/${groupId}`, payload)
  }

  deleteGroup(groupId: string): Observable<void> {
    return this.delete(`${this.url}/${groupId}`)
  }

  getGroup(groupId: string): Observable<AccessManagementGroup> {
    return this.get(`${this.url}/${groupId}`)
  }

  getGroupUsers(groupId: string): Observable<AccessManagementUser[]> {
    return this.get(`${this.url}/${groupId}/members`)
  }

  getGroupUsersPaginated(
    groupId: string,
    params: PaginationQueryParams
  ): Observable<PaginatedResponse<AccessManagementUser>> {
    return this.get(`${this.url}/${groupId}/members`, {
      params: this.addParams({ ...DEFAULT_PAGINATION_PARAMS, ...params }),
    })
  }

  addUsersToGroup(
    groupId: string,
    users: AccessManagementUserSummary[],
    newUserEmails?: string[]
  ): Observable<AccessManagementUser[]> {
    return this.post(`${this.url}/${groupId}/members/bulk`, {
      ...(users.length && { userSubjectIds: users.map((user) => user.subjectId) }),
      ...(newUserEmails?.length && { newUserEmails }),
    })
  }

  removeUserFromGroup(
    groupId: string,
    user: AccessManagementUser,
    force = false
  ): Observable<void> {
    return this.delete(
      `${this.url}/${groupId}/members/${user.subjectId}`,
      force ? { params: { force } } : undefined
    )
  }

  getGroupAssetSummaries(groupId: string): Observable<AccessManagementAssetSummary[]> {
    return this.getGroupSolutionAccessAssets(groupId, '').pipe(
      map((assets) =>
        assets.map(({ assetId: id, assetName: name, assetType: type }) => ({
          id,
          name,
          type,
          applicationType: ApplicationType.APP,
        }))
      )
    )
  }

  getGroupMemberCandidates(
    groupId: string,
    filterKey: string
  ): Observable<AccessManagementUserSummary[]> {
    return this.get(`${this.url}/${groupId}/member-candidates`, {
      params: this.addParams({ filterKey }),
    })
  }

  getGroupSolutionAccessAssets(
    groupId: string,
    filterKey: string
  ): Observable<GroupSolutionAccessAsset[]> {
    return this.get(`${this.url}/${groupId}/assets`, {
      params: this.addParams({ filterKey }),
    })
  }

  getGroupAssetCandidates(
    groupId: string,
    filterKey: string
  ): Observable<GroupSolutionAccessAssetCandidate[]> {
    return this.get(`${this.url}/${groupId}/asset-candidates`, {
      params: this.addParams({ filterKey }),
    })
  }

  getAllGroups(filterKey: string): Observable<AccessManagementGroup[]> {
    return this.get(`${this.url}/all`, {
      params: this.addParams({ filterKey }),
    })
  }

  getGroupsByIds(groupsIds: string[]): Observable<AccessManagementGroup[]> {
    return this.post(`${this.url}/by-ids`, { groupsIds })
  }
}
